import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Field, Notice, Spinner } from '../components/ui';
import { CountryField, EducationSettingField, PriorityAreasField, ChipChoice, STAFF_LABEL } from '../components/OrgFields';
import { ApiError } from '../lib/api';
import { areasOf, getProfile, saveProfile, settingOptions, suggestionsFor, type PriorityArea } from '../lib/onboarding';
import { useSchool } from '../lib/school';
import { usePeople } from '../lib/people';
import { ScoreModeSetting } from '../components/ScoreMode';
import { TermDatesField, termDatesProblem, type TermDate } from '../components/TermDates';
import { useSession } from '../lib/session';
import '../components/Form.css';
import './Page.css';

type Draft = {
  name: string;
  phase: string | null;
  phaseOther: string;
  country: string | null;
  staffBand: string | null;
  trustName: string;
  priorities: PriorityArea[];
  terms: TermDate[];
};

const clean = (areas: PriorityArea[]) => areas.filter((a) => a.text.trim()).map((a) => ({ text: a.text.trim(), suggestion: a.suggestion }));

/**
 * Organisation settings: every setup answer, editable. Changes are kept as a
 * draft until Save; an "Unsaved changes" bar shows meanwhile, and leaving the
 * page (or the tab) with a draft asks first.
 */
export function SchoolSettingsPage() {

  const { refresh } = useSession();
  const qc = useQueryClient();
  const school = useSchool();
  const people = usePeople();
  const profile = useQuery({ queryKey: ['schoolProfile'], queryFn: getProfile });

  const [saved, setSaved] = useState<Draft | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [countryOther, setCountryOther] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ tone: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    const p = profile.data;
    if (!p || !school.data) return;
    const d: Draft = {
      name: school.data.name,
      phase: p.phase,
      phaseOther: p.phaseOther ?? '',
      country: p.country,
      staffBand: p.staffBand,
      trustName: p.trustName ?? '',
      priorities: areasOf(p),
      terms: p.termDates ?? [],
    };
    setSaved(d);
    setDraft(d);
  }, [profile.data, school.data]);

  const dirty = useMemo(() => {
    if (!draft || !saved) return false;
    const norm = (d: Draft) => JSON.stringify({ ...d, priorities: clean(d.priorities), name: d.name.trim(), trustName: d.trustName.trim() });
    return norm(draft) !== norm(saved);
  }, [draft, saved]);

  // Closing or reloading the tab with unsaved edits asks first.
  useEffect(() => {
    if (!dirty) return;
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    // Links inside the app (sidebar, menu) too.
    const onClick = (e: MouseEvent) => {
      const a = (e.target as HTMLElement).closest('a[href]');
      if (!a || a.getAttribute('target') === '_blank') return;
      if (!window.confirm('You have unsaved changes. Leave without saving?')) {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    document.addEventListener('click', onClick, true);
    return () => {
      window.removeEventListener('beforeunload', warn);
      document.removeEventListener('click', onClick, true);
    };
  }, [dirty]);

  const set = <K extends keyof Draft>(key: K, value: Draft[K]) => {
    setMessage(null);
    setDraft((d) => (d ? { ...d, [key]: value } : d));
  };

  const all = profile.data?.options.allCountries;
  const problem = !draft
    ? null
    : !draft.name.trim()
      ? 'Your organisation needs a name.'
      : draft.phase === 'other' && !draft.phaseOther.trim()
        ? 'Tell us your setting.'
        : !draft.country
          ? 'Choose your country.'
          : all && !all.includes(draft.country)
            ? 'Pick your country from the list.'
            : !clean(draft.priorities).length
              ? 'Add at least one priority area.'
              : termDatesProblem(draft.terms);

  const save = async (e?: FormEvent) => {
    e?.preventDefault();
    if (!draft || problem) return;
    setMessage(null);
    setSaving(true);
    try {
      await saveProfile({
        name: draft.name.trim(),
        phase: draft.phase,
        phaseOther: draft.phaseOther,
        country: draft.country,
        staffBand: draft.staffBand,
        trustName: draft.trustName,
        priorities: clean(draft.priorities),
        // Older APIs don't know term dates; only send them once there are some.
        ...(draft.terms.length ? { termDates: draft.terms.map((t) => ({ ...t, name: t.name.trim() })) } : {}),
      });
      await Promise.all([qc.invalidateQueries({ queryKey: ['school'] }), qc.invalidateQueries({ queryKey: ['schoolProfile'] }), refresh()]);
      setMessage({ tone: 'success', text: 'Saved.' });
    } catch (err) {
      setMessage({ tone: 'error', text: err instanceof ApiError ? err.message : 'That didn’t save. Try again.' });
    } finally {
      setSaving(false);
    }
  };


  if (school.isLoading || profile.isLoading || !draft) return <Spinner />;
  const o = profile.data?.options;

  return (
    <div className="page">
      <header className="page-head">
        <h1>Organisation settings</h1>
        <p className="muted">What EducAitor knows about {school.data?.name ?? 'your organisation'}. Save to apply your changes.</p>
      </header>

      <div aria-live="polite">{message ? <Notice tone={message.tone}>{message.text}</Notice> : null}</div>

      <form className="page" onSubmit={save} noValidate>
        <section className="panel form-section">
          <h2>About the organisation</h2>
          <div className="form-grid">
            <Field id="org-name" label="Organisation name" value={draft.name} onChange={(e) => set('name', e.target.value)} />
            <Field id="org-trust" label="Trust or group (optional)" value={draft.trustName} onChange={(e) => set('trustName', e.target.value)} />
          </div>
          <EducationSettingField
            options={settingOptions(profile.data)}
            value={draft.phase}
            other={draft.phaseOther}
            onChange={(v) => set('phase', v)}
            onOtherChange={(v) => set('phaseOther', v)}
          />
          <CountryField
            quick={o?.countries ?? []}
            all={o?.allCountries ?? o?.countries ?? []}
            value={draft.country}
            onChange={(v) => set('country', v)}
            otherOpen={countryOther}
            onOtherOpen={setCountryOther}
          />
          <ChipChoice
            label={STAFF_LABEL}
            options={(o?.staffBands ?? []).map((v) => ({ value: v, label: v }))}
            value={draft.staffBand}
            onChange={(v) => set('staffBand', v)}
          />
        </section>

        <section className="panel form-section">
          <div>
            <h2>Leadership Priority Areas</h2>
            <p className="muted">The Leadership Hub tracks these first.</p>
          </div>
          <PriorityAreasField areas={draft.priorities} onChange={(v) => set('priorities', v)} suggestions={suggestionsFor(profile.data, draft.phase)} />
        </section>

        <section className="panel form-section">
          <div>
            <h2>Term dates</h2>
            <p className="muted">
              The Leadership Hub&rsquo;s &ldquo;this term&rdquo; and term-by-term comparisons, and teachers&rsquo; term look back, follow these. Add next
              year&rsquo;s before it starts.
            </p>
          </div>
          <TermDatesField value={draft.terms} onChange={(v) => set('terms', v)} />
        </section>

        {dirty ? (
          <div className="unsaved-bar" role="status">
            <span>{problem ?? 'Unsaved changes'}</span>
            <div className="unsaved-actions">
              <Button type="button" variant="ghost" onClick={() => saved && setDraft(saved)} disabled={saving}>
                Discard
              </Button>
              <Button type="submit" loading={saving} disabled={!!problem}>
                Save
              </Button>
            </div>
          </div>
        ) : null}
      </form>

      <section className="panel form-section">
        <div>
          <h2>How scores are shown</h2>
          <p className="muted">
            Every lesson gets written feedback: strengths, next steps and quotes from the lesson. This decides who also sees the numbers. Changes apply
            straight away, and everyone in {school.data?.name ?? 'your organisation'} is told in the app.
          </p>
        </div>
        <ScoreModeSetting mode={school.data?.score_mode ?? 'team'} canChange={!!people.data?.me.isOwner} />
      </section>

      <p className="muted">
        The organisation code and who can join are on <Link to="/people">People</Link>.
      </p>
    </div>
  );
}
